import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Trash2, Banknote, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/context/CartContext";
import { useApi, errMsg, sar } from "@/lib/api";
import { Empty } from "@/components/common";

const Line = ({ item, i, cart }) => (
  <div data-testid={`cart-item-${i}`} className="flex gap-3 rounded-xl border border-[#E5DEC9] bg-white p-3">
    <img src={item.product.image} alt={item.product.name} className="h-16 w-16 rounded-lg object-cover" />
    <div className="flex-1 min-w-0">
      <p className="truncate font-bold text-sm text-[#0F382C]">{item.product.name}</p>
      <p className="text-xs text-[#5C7067]">
        {sar(item.product.price)} {item.with_installation && <>+ تركيب {sar(item.product.installation_fee)}</>}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <button data-testid={`cart-item-dec-${i}`} onClick={() => cart.setQty(i, item.qty - 1)} className="rounded-md border p-1"><Minus className="h-3 w-3" /></button>
        <span className="w-6 text-center text-sm font-bold" data-testid={`cart-item-qty-${i}`}>{item.qty}</span>
        <button data-testid={`cart-item-inc-${i}`} onClick={() => cart.setQty(i, item.qty + 1)} className="rounded-md border p-1"><Plus className="h-3 w-3" /></button>
        <button data-testid={`cart-item-remove-${i}`} onClick={() => cart.remove(i)} className="mr-auto text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);

export default function CartSheet() {
  const cart = useCart();
  const api = useApi();
  const navigate = useNavigate();
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    setBusy(true);
    try {
      const order = await api("post", "/owner/orders", {
        items: cart.items.map((i) => ({ product_id: i.product.id, qty: i.qty, with_installation: i.with_installation })),
        preferred_date: date,
        notes,
      });
      cart.clear();
      cart.setOpen(false);
      setNotes("");
      toast.success(`تم إرسال طلبك بنجاح — رقم الطلب ${order.code}`);
      navigate("/owner/orders");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={cart.open} onOpenChange={cart.setOpen}>
      <SheetContent side="left" dir="rtl" className="flex w-full flex-col bg-[#FAF7F2] sm:max-w-md" data-testid="cart-sheet">
        <SheetHeader className="text-right">
          <SheetTitle className="text-[#0F382C]">سلة المشتريات</SheetTitle>
        </SheetHeader>
        {cart.items.length === 0 ? (
          <div className="mt-6"><Empty icon={ShoppingBag} title="السلة فارغة" text="أضف منتجات من متجر الخدمات الشخصية" testId="cart-empty" /></div>
        ) : (
          <>
            <div className="mt-4 flex-1 space-y-3 overflow-y-auto">
              {cart.items.map((it, i) => <Line key={i} item={it} i={i} cart={cart} />)}
              <div className="space-y-2 pt-2">
                <Label htmlFor="pref-date">موعد التنفيذ المفضل</Label>
                <Input id="pref-date" type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="checkout-date-input" />
                <Label htmlFor="notes">ملاحظات</Label>
                <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-notes-input" placeholder="مثال: الاتصال قبل الحضور" />
              </div>
            </div>
            <div className="space-y-2 border-t border-[#E5DEC9] pt-4 text-sm">
              <div className="flex justify-between"><span>المنتجات</span><span data-testid="cart-subtotal">{sar(cart.totals.subtotal)}</span></div>
              <div className="flex justify-between"><span>رسوم التركيب</span><span data-testid="cart-installation">{sar(cart.totals.installation)}</span></div>
              <div className="flex justify-between text-base font-extrabold text-[#0F382C]"><span>الإجمالي</span><span data-testid="cart-total">{sar(cart.totals.total)}</span></div>
              <div className="flex items-center gap-2 rounded-lg bg-[#0F382C]/5 p-3 text-[#0F382C]">
                <Banknote className="h-4 w-4" /> الدفع عند التنفيذ — لا يتم تحصيل أي مبلغ إلكترونياً
              </div>
              <Button data-testid="checkout-submit-button" disabled={busy} onClick={checkout} className="h-12 w-full rounded-full bg-[#0F382C] text-base hover:bg-[#164E3D]">
                {busy ? "جاري الإرسال..." : "تأكيد الطلب"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
